import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications } from "@/context/NotificationsContext";
import { useI18n } from "@/context/I18nContext";

function relTime(iso, nowLabel) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return nowLabel;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationBell({ onNavigate }) {
  const { t } = useI18n();
  const { items, unread, markAllRead, markRead } = useNotifications();
  const navigate = useNavigate();

  const open = (n) => {
    markRead(n.notification_id);
    onNavigate?.();
    navigate(`/app/community?post=${n.post_id}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          aria-label={t("notif.title")}
          className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          <Bell className="h-5 w-5" strokeWidth={1.5} />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <span className="font-serif text-base">{t("notif.title")}</span>
          {unread > 0 && (
            <button onClick={markAllRead} data-testid="notifications-mark-all"
                    className="text-xs text-primary hover:underline">
              {t("notif.markAll")}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
            {t("notif.empty")}
          </p>
        ) : (
          <ScrollArea className="max-h-96">
            <div className="divide-y divide-border/50">
              {items.map((n) => {
                const Icon = n.type === "like" ? Heart : MessageCircle;
                return (
                  <button
                    key={n.notification_id}
                    onClick={() => open(n)}
                    data-testid={`notification-${n.notification_id}`}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/40 ${n.read ? "" : "bg-primary/5"}`}
                  >
                    <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${n.type === "like" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                      <Icon className={`h-4 w-4 ${n.type === "like" ? "fill-current" : ""}`} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{n.actor_name}</span>{" "}
                        <span className="text-muted-foreground">{t(n.type === "like" ? "notif.liked" : "notif.replied")}</span>
                      </p>
                      {n.snippet && <p className="mt-0.5 truncate text-xs text-muted-foreground">“{n.snippet}”</p>}
                      <p className="mt-0.5 text-[11px] text-muted-foreground/70">{relTime(n.created_at, t("notif.now"))}</p>
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
